import { GetServerSidePropsContext } from 'next'
import { Product } from '@mc/api'
import { BogoBanner } from '@mc/bogo'
import { reportError } from '@mc/next-newrelic'
import { paths } from '@mc/paths'
import { PAGE_SOURCES, PromoExplainerModal } from '@mc/promos'
import { StaticDataProvider } from '@mc/static-data-react'
import { TRACK_PROPS } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { RegularFooter } from 'components/Footer/RegularFooter'
import { CoreNavigation } from 'components/Navigation/consumer'
import { trackPen7694BackendEV } from 'experiments/pen-7694_v1_promo_sticky_footer_gift_cta_routing/server'
import {
  getDefaultProduct,
  getPrimarySubcategoryForOutcome,
  getOutcomeContent,
  outcomeExists,
} from 'libs/server/api'
import {
  getGrowthClassesBySubcategoryIds,
  getGrowthPrimaryCategoryBySubcategorySlug,
  getGrowthSubcategoryBySlug,
  getV2Categories,
} from 'libs/server/api/growth'
import { getPageData } from 'libs/server/page-data'
import { pageErrorHandler } from 'libs/server/page-error-handler'
import { redirect } from 'libs/utils/redirect'
import { CourseMeta, Outcome, OutcomeContent } from 'types/data'
import { getRankedContent } from '../../libs/utils'
import { Meta } from './components/Meta'
import { OutcomePage } from './components/OutcomePage'
import { VideoContextProvider } from './components/VideoContext'
import { TrackingAttributes, TrackPageViewed } from './tracking'

type Ret<T extends (...args: never[]) => unknown> = Awaited<ReturnType<T>>

interface OutcomesProps {
  outcomeSlug: string
  subcatSlug: string
  courseSlugToMeta: Record<string, CourseMeta>
  primaryCategory: Ret<typeof getGrowthPrimaryCategoryBySubcategorySlug>
  primarySubcategory: Ret<typeof getGrowthSubcategoryBySlug>
  otherClassesInSameSubcategories: Ret<typeof getGrowthClassesBySubcategoryIds>
  outcome: Outcome
  outcomeRankedContent: OutcomeContent[]
  annualPass: Product
}

const Outcomes = ({
  outcomeSlug,
  subcatSlug,
  courseSlugToMeta,
  primaryCategory,
  primarySubcategory,
  otherClassesInSameSubcategories,
  outcome,
  outcomeRankedContent,
  annualPass,
}: OutcomesProps) => (
  <TrackingParamsProvider value={{ [TRACK_PROPS.PAGE_TYPE]: 'Outcome' }}>
    <TrackPageViewed name='Outcome' />
    <TrackingAttributes page_source='outcome' outcomeName={outcome.name}>
      <StaticDataProvider
        value={{
          outcomeSlug,
          subcatSlug,
          annualPass,
          courseSlugToMeta,
          primaryCategory,
          primarySubcategory,
          outcome,
          outcomeRankedContent,
          otherClassesInSameSubcategories,
        }}
        providerKey={`outcome-${outcomeSlug}`}
      >
        <VideoContextProvider>
          <PromoExplainerModal pageSource={PAGE_SOURCES.OUTCOME} />
          <BogoBanner />
          <Meta />
          <OutcomePage />
        </VideoContextProvider>
      </StaticDataProvider>
    </TrackingAttributes>
  </TrackingParamsProvider>
)

Outcomes.pageSettings = {
  rumLabel: 'Outcome',
  navigation: CoreNavigation,
  footer: RegularFooter,
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  // Initial validation
  const outcomeSlug = ctx.query.slug as string
  const isOutcomeValid = await outcomeExists(outcomeSlug)

  if (!isOutcomeValid) {
    return redirect(ctx, paths.categories, { queryExclude: ['slug'] })
  }

  // Get extra data
  const subcatSlug = await getPrimarySubcategoryForOutcome(outcomeSlug)

  const { outcome, courseSlugToMeta } = await getOutcomeContent(
    ctx,
    outcomeSlug,
  )

  const primaryCategory = await getGrowthPrimaryCategoryBySubcategorySlug(
    ctx,
    subcatSlug,
  )
  const primarySubcategory = await getGrowthSubcategoryBySlug(ctx, subcatSlug)

  if (
    !primaryCategory ||
    !primarySubcategory ||
    Object.keys(primaryCategory).length === 0
  ) {
    return redirect(ctx, paths.categories, { queryExclude: ['slug'] })
  }

  await trackPen7694BackendEV(ctx)

  const outcomeRankedContent = getRankedContent(outcome.contents)

  try {
    return getPageData({
      nonCriticalPromises: {
        annualPass: getDefaultProduct(ctx),
        categories: getV2Categories(ctx),
        otherClassesInSameSubcategories: getGrowthClassesBySubcategoryIds(ctx, [
          primarySubcategory.id,
        ]).catch(() => []),
      },
      propsMapper: (restProps) => ({
        outcomeSlug,
        subcatSlug,
        primaryCategory,
        primarySubcategory,
        courseSlugToMeta,
        outcome,
        outcomeRankedContent,
        ...restProps,
      }),
    })
  } catch (e) {
    reportError(e as Error, { slug: outcomeSlug })
    return pageErrorHandler(e)
  }
}

export default Outcomes
